import { FormikProps } from 'formik';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ActionButtonGroup, Responsive, SettingsButtons, TabContainer } from '@components';
import { CacheTab, FormActionType, FormType, Translation } from '@enums';
import {
  useFetchFormList,
  useFormHandlers,
  useFormLoadingStatus,
  useFormProcedure,
  useFormSubmissionStatus,
} from '@hooks';
import { getCacheTabs, validateValueEqual } from '@utils';

import { PredefinedCacheView } from './subCards';

export const Cache: FC = () => {
  const { id: application_id } = useParams();

  const predefinedCacheFormType = FormType.CdnCache;

  const { t: tConfigurationButton } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons' });

  const formProcedure = useFormProcedure(predefinedCacheFormType);
  const formSubmitting = useFormSubmissionStatus({ formType: predefinedCacheFormType });
  const formLoading = useFormLoadingStatus({ formType: predefinedCacheFormType });

  const formikRef = useRef<FormikProps<any> | null>(null);
  const [activeTab, setActiveTab] = useState<CacheTab>(CacheTab.CACHE_RULES);

  useFetchFormList({
    formType: predefinedCacheFormType,
    params: { application_id },
    globalOptions: {
      content: true,
    },
  });

  const handleFormikInstance = (formik) => {
    formikRef.current = formik;
  };

  const handleSaveSettings = useCallback(() => {
    formikRef?.current?.handleSubmit();
  }, []);

  const CacheFormHandler = useFormHandlers({
    formType: predefinedCacheFormType,
    handlerOptions: [
      {
        type: FormActionType.Save,
        function: handleSaveSettings,
        label: tConfigurationButton('saveSettings'),
        loading: formSubmitting,
        disabled: formLoading,
      },
    ],
  });

  const contentConfig = useCallback(() => {
    return {
      [CacheTab.CACHE_RULES]: (
        <div className="flex flex-col gap-8">
          <PredefinedCacheView formikRef={formikRef} onFormikInstance={handleFormikInstance} />
        </div>
      ),
    };
  }, []);

  const CacheTabs = useMemo(() => getCacheTabs(), [tConfigurationButton]);

  return (
    <div className=" w-full flex flex-col flex-1 gap-6 sm:gap-8">
      <TabContainer<CacheTab>
        tabs={CacheTabs}
        onTabChange={setActiveTab}
        contentConfig={contentConfig()}
        adornment={
          <Responsive showAbove="sm">
            {validateValueEqual(activeTab, CacheTab.CACHE_RULES) && (
              <ActionButtonGroup actionHandlers={CacheFormHandler} procedure={formProcedure} />
            )}
          </Responsive>
        }
      />
      <Responsive showBelow="sm">
        {validateValueEqual(activeTab, CacheTab.CACHE_RULES) && (
          <SettingsButtons handleSaveModal={handleSaveSettings} formType={predefinedCacheFormType} />
        )}
      </Responsive>
    </div>
  );
};
